import React, { useState, useRef, useCallback, useEffect } from 'react';
// @ts-ignore
import { BarChart3, Users, MapPin, Package, Radio, AlertTriangle, Maximize, Minimize } from 'lucide-react';
import OperationalDashboardOverview from './OperationalDashboardOverview';
import OperationalDashboardConferentes from './OperationalDashboardConferentes';
import OperationalDashboardRegions from './OperationalDashboardRegions';
import OperationalDashboardCargas from './OperationalDashboardCargas';
import OperationalDashboardRealtime from './OperationalDashboardRealtime';
import OperationalDashboardAlerts from './OperationalDashboardAlerts';
import '../../styles/operationalDashboard.css';

type Tab = 'overview' | 'conferentes' | 'regions' | 'cargas' | 'realtime' | 'alerts';

interface TabDef { key: Tab; label: string; icon: React.ReactNode }

const TABS: TabDef[] = [
  { key: 'realtime', label: 'Tempo Real', icon: <Radio size={16} /> },
  { key: 'overview', label: 'Visão Geral', icon: <BarChart3 size={16} /> },
  { key: 'conferentes', label: 'Conferentes', icon: <Users size={16} /> },
  { key: 'regions', label: 'Regiões', icon: <MapPin size={16} /> },
  { key: 'cargas', label: 'Paletes', icon: <Package size={16} /> },
  { key: 'alerts', label: 'Alertas', icon: <AlertTriangle size={16} /> },
];

const OperationalDashboardLayout: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>('realtime');
  const [periodo, setPeriodo] = useState('hoje');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const toggleFullscreen = useCallback(() => {
    if (!contentRef.current) return;

    if (!document.fullscreenElement) {
      contentRef.current.requestFullscreen().catch(() => {});
    } else {
      document.exitFullscreen().catch(() => {});
    }
  }, []);

  useEffect(() => {
    const handleChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleChange);
    return () => document.removeEventListener('fullscreenchange', handleChange);
  }, []);

  const handlePeriodoChange = (p: string) => {
    setPeriodo(p);
    setDataInicio('');
    setDataFim('');
  };

  const handleCustomDate = () => {
    if (dataInicio && dataFim) {
      if (new Date(dataInicio) > new Date(dataFim)) {
        setDataInicio(dataFim);
        setDataFim(dataInicio);
      }
      setPeriodo('personalizado');
    }
  };

  const filters = periodo === 'personalizado' && dataInicio && dataFim
    ? { periodo: 'personalizado', dataInicio, dataFim }
    : { periodo };

  const showFilters = activeTab !== 'realtime' && activeTab !== 'alerts';

  const renderContent = () => {
    switch (activeTab) {
      case 'overview': return <OperationalDashboardOverview filters={filters} />;
      case 'conferentes': return <OperationalDashboardConferentes filters={filters} />;
      case 'regions': return <OperationalDashboardRegions filters={filters} />;
      case 'cargas': return <OperationalDashboardCargas filters={filters} />;
      case 'realtime': return <OperationalDashboardRealtime />;
      case 'alerts': return <OperationalDashboardAlerts />;
      default: return null;
    }
  };

  return (
    <div className="op-dashboard">
      <div className="op-dashboard-header">
        <h1 className="op-dashboard-title">
          <BarChart3 size={22} />
          Dashboard Operacional
        </h1>
        <span className="op-dashboard-subtitle">Análise de Performance da Operação</span>
      </div>

      <nav className="op-dashboard-nav">
        {TABS.map(tab => (
          <button
            key={tab.key}
            className={`op-dashboard-nav-item ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </nav>

      {showFilters && (
        <div className="op-dashboard-filters">
          {['hoje', 'semana', 'mes'].map(p => (
            <button
              key={p}
              className={`op-dashboard-filter-btn ${periodo === p ? 'active' : ''}`}
              onClick={() => handlePeriodoChange(p)}
            >
              {p === 'hoje' ? 'Hoje' : p === 'semana' ? 'Semana' : 'Mês'}
            </button>
          ))}
          <div className="op-dashboard-filter-custom">
            <input
              type="date"
              className="op-dashboard-date-input"
              value={dataInicio}
              onChange={e => setDataInicio(e.target.value)}
            />
            <span className="op-dashboard-date-sep">até</span>
            <input
              type="date"
              className="op-dashboard-date-input"
              value={dataFim}
              onChange={e => setDataFim(e.target.value)}
            />
            <button
              className={`op-dashboard-filter-btn ${periodo === 'personalizado' ? 'active' : ''}`}
              onClick={handleCustomDate}
              disabled={!dataInicio || !dataFim}
            >
              Filtrar
            </button>
          </div>
        </div>
      )}

      <div
        ref={contentRef}
        className={`op-dashboard-content${isFullscreen ? ' op-dashboard-fullscreen' : ''}`}
      >
        <button
          className="op-dashboard-fullscreen-btn"
          onClick={toggleFullscreen}
          title={isFullscreen ? 'Sair da tela cheia (ESC)' : 'Tela cheia (ideal para TV)'}
        >
          {isFullscreen ? <Minimize size={18} /> : <Maximize size={18} />}
          {isFullscreen ? 'Sair da Tela Cheia' : 'Tela Cheia'}
        </button>
        {renderContent()}
      </div>
    </div>
  );
};

export default OperationalDashboardLayout;
